import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CalendarEvent, CalendarMonthViewDay, CalendarView } from 'angular-calendar';
import { isSameDay, isSameMonth } from 'date-fns';
import dayjs from 'dayjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { takeUntil } from 'rxjs';
import { AnalyticsService } from '../../@core/utils';
import { UPCOMING_EVENTS } from '../../config/mock-data';
import { ROLE, ROLES } from '../../enum/roles.enum';
import { ClearObservableHelper } from '../../helpers/ClearObservableHelper';
import { AdminSchoolEvent } from '../../interface/admin-school-event';
import { Classroom } from '../../interface/calendar';
import { School } from '../../interface/school';
import { User, UserData } from '../../interface/user';
import { AcademicYearService } from '../../services/academic-year.service';
import { AuthService } from '../../services/auth.service';
import { CalendarService } from '../../services/calendar.service';
import { RoleService } from '../../services/role.service';
import { SchoolService } from '../../services/school.service';
import { StorageService } from '../../services/storage.service';
import { StudentService } from '../../services/student.service';
@Component({
  selector: 'ngx-admin-school-events',
  templateUrl: './admin-school-events.component.html',
  styleUrls: ['./admin-school-events.component.scss'],
})
export class AdminSchoolEventsComponent
  extends ClearObservableHelper
  implements OnInit, AfterContentChecked, OnDestroy
{
  @Input() isStudent = false;
  @Input() isExpand = false;
  @Input() isTeacherView = false;
  @Input() classroom: Classroom;
  @Input() isAdmin: boolean;
  @Input() isTeacher: boolean;
  @Input() currentUser: User;
  @Input() isDashboard = false;

  CalendarView = CalendarView;
  viewDate = new Date();
  calendarViewType = CalendarView.Month;
  user: UserData | any;
  userStoredData: UserData | any;
  currentSchool: School | any;
  @ViewChild('myModal') myModal;
  startDate: Date = new Date();
  endDate: Date;
  users: User[];
  loading = false;
  isSuperAdmin = false;
  upcomingEvents: any[] = [];
  activeDayIsOpen = false;
  isEditEvent = false;
  eventId: number;
  roles = [];
  setCurrentDate: Date;
  isCalendarStart = false;
  isCalendarEnd = false;
  private dialogRef: NbDialogRef<any> | null = null;
  public isMobileScreen = window.innerWidth <= 768;
  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.isMobileScreen = event.target.innerWidth <= 768;
  }
  currentValuesToPass: any;
  public isOpenModal: boolean = false;
  mockData = this.storageService.getItem('user').user['is_trial_user'] ? true : false;
  @ViewChild('viewEventModal') viewEventModal: TemplateRef<any>;
  modalInstance: any;
  tabTitle: string;
  hasExcuted = false;

  constructor(
    private roleService: RoleService,
    public authService: AuthService,
    public storageService: StorageService,
    private schoolService: SchoolService,
    public modalService: NgbModal,
    private calendarService: CalendarService,
    private route: ActivatedRoute,
    private changeDetector: ChangeDetectorRef,
    public analyticsService: AnalyticsService,
    private studentService: StudentService,
    private spinner: NgxSpinnerService,
    public router: Router,
    public dialog: MatDialog,
    private dialogService: NbDialogService,
    public academicService: AcademicYearService,
  ) {
    super();
  }

  async ngOnInit() {
    try {
      this.studentService.currentClassroom.pipe(takeUntil(this.destroy$)).subscribe(async (classroom) => {
        if (!this.hasExcuted) {
          this.classroom = classroom;
          this.hasExcuted = true;
          await this.getUpcomingEvents();
          setTimeout(() => {
            this.hasExcuted = false;
          }, 2000);
        }
      });

      // Subscribed when the selected school is changed from the header
      this.storageService.setSelectedSchoolsToLocalStorage.pipe(takeUntil(this.destroy$)).subscribe(async (res) => {
        if (res) {
          this.spinner.show();
          await this.getUpcomingEvents();
          this.spinner.hide();
        }
      });

      // Subscribed when the academic year (school calendar) is changed from the header
      this.storageService.setSelectedSchoolsCalendarToLocalStorage
        .pipe(takeUntil(this.destroy$))
        .subscribe(async () => {
          // Waits for 1 second before proceeding to allow any changes to be fully applied.
          // This delay ensures that the calendar and default classroom are properly set before fetching new data.
          setTimeout(async () => {
            this.spinner.show();
            await this.getUpcomingEvents();
            this.spinner.hide();
          }, 1000);
        });

      this.spinner.show();
      if (!this.isDashboard) this.spinner.show();
      this.loading = true;
      this.viewDate = this.calendarService.schoolEventDate ? this.calendarService.schoolEventDate : new Date();

      const currentId = Number(this.route.snapshot.paramMap.get('id'));
      this.userStoredData = this.storageService.getItem('user');

      if (currentId) {
        this.isSuperAdmin = true;
        this.user = await this.getUserById(currentId);
      } else {
        this.isSuperAdmin = false;
        this.user = this.currentUser ? { authToken: '', user: this.currentUser } : this.storageService.getItem('user');
      }

      await this.getCurrentSchoolCalendar();
      await this.getUpcomingEvents();
      if (this.isTeacher) {
        this.upcomingEvents = this.upcomingEvents.filter((event) => {
          return event.user_id !== (this.user && this.user.user && this.user.user.id);
        });
      }

      this.authService.closeDialog.subscribe(async (x) => {
        if (x === true) {
          await this.getUpcomingEvents();
        }
      });

      this.authService.currentStudent.subscribe(async (x) => {
        if (x && x.id) {
          this.loading = true;
          this.user = { authToken: '', user: x };
          await this.getCurrentSchoolCalendar();
          await this.getUpcomingEvents();
          this.spinner.hide();
        }
      });
      this.spinner.hide();
      if (!this.isDashboard) this.spinner.hide();
      this.roles = await this.roleService.getAllRoles();
      //Removed the teacher only and super admin role from the filter list
      this.roles = this.roles.filter(
        (role) =>
          !this.storageService.checkUserRole(role.id, ROLE.TEACHER_ONLY) &&
          !this.storageService.checkUserRole(role.id, ROLE.SUPER_ADMIN),
      );
      if (this.isTeacher) {
        this.roles = this.roles.filter((obj) => obj.name === 'Student' || obj.name === 'Parent');
      }
    } catch (error) {
      this.spinner.hide();
      this.loading = false;
      // console.log(error);
    }
  }

  async getUserById(id) {
    try {
      const user = await this.authService.userById(id);
      return {
        authToken: null,
        user: user,
      };
    } catch (error) {
      // console.log(error);
    }
  }

  async getCurrentSchoolCalendar() {
    try {
      this.currentSchool = await this.schoolService.schoolById(this.user.user.school_id);
      const currentSelectedYear = (await this.storageService.getItem('selectedSchoolCalendarByAdmin')) || {};
      if (this.currentSchool && this.currentSchool.id) {
        // If mockData is enabled, use the static dates from currentSelectedYear. Otherwise, use the actual school start and end dates.
        this.startDate = this.mockData ? new Date(currentSelectedYear.start) : new Date(this.currentSchool.start_date);
        this.endDate = this.mockData ? new Date(currentSelectedYear.end) : new Date(this.currentSchool.end_date);
      }
    } catch (error) {
      // console.log(error);
    }
  }

  dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
    if (isSameMonth(date, this.viewDate)) {
      if ((isSameDay(this.viewDate, date) && this.activeDayIsOpen === true) || events.length === 0) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
      }
      this.viewDate = date;
    }
  }

  async getUsersByRole() {
    try {
      const school = this.storageService.getItem('selectedSchoolByAdmin');
      const body = {
        school_id: school?.id ?? this.user.user.school_id,
      };
      this.users = (await this.authService.userByRoleId(body)) as any;
      this.users.splice(
        this.users.findIndex((user) => user.id === this.user.user.id),
        1,
      );
    } catch (error) {
      // console.log(error);
    }
  }

  async updateSchoolEvents() {
    try {
      return this.upcomingEvents.map((x, i) => {
        (x.start = new Date(x.start)), (x.end = new Date(x.end));
        x['cssClass'] = this.getEventClass(i + 1);
        if ((x.isDummy = true)) {
          x.start.setMonth(this.viewDate.getMonth());
        }
        return x;
      });
    } catch (error) {
      // console.log(error);
    }
  }

  getEventClass(index: number) {
    switch (index) {
      case 1:
      case 5:
      case 9:
      case 13:
      case 17:
      case 21:
      case 25:
      case 29:
        return 'green-event-content';
      case 3:
      case 7:
      case 11:
      case 15:
      case 19:
      case 23:
      case 27:
      case 31:
        return 'blue-event-content';
      case 4:
      case 8:
      case 12:
      case 16:
      case 20:
      case 24:
      case 28:
        return 'purple-event-content';
      case 2:
      case 6:
      case 10:
      case 14:
      case 18:
      case 22:
      case 26:
      case 30:
        return 'yellow-event-content';
    }
  }

  async openEventModal(event?) {
    try {
      // this.dialog.closeAll();
      this.loading = true;
      this.analyticsService?.trackEvent('Admin School Modal', 'Admin School Events');
      if (this.storageService.detectMob()) {
        this.dialog.closeAll();
        event
          ? this.router.navigate(['/dashboard/admin-events', event.id])
          : this.router.navigateByUrl('dashboard/admin-events');
        return;
      }

      this.isOpenModal = false;
      await this.getUsersByRole();

      if (event) {
        this.loading = true;
        this.isEditEvent = true;
        this.eventId = event.id;
      } else {
        this.loading = true;
        this.setCurrentDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), 1);

        this.isEditEvent = false;
      }

      this.currentValuesToPass = {
        users: this.users,
        user: this.user,
        adminEvent: event,
        isEditEvent: this.isEditEvent,
        roles: this.roles,
      };
      this.isOpenModal = true;

      if (!this.dialogRef) {
        this.dialogRef = this.dialogService.open(this.viewEventModal, {
          closeOnBackdropClick: false,
        });
        this.dialogRef.onClose.subscribe(() => {
          this.dialogRef = null;
        });
      }
      // this.modalService.open(this.myModal, {
      //   size: 'lg',
      //   modalDialogClass: 'mymodal',
      //   centered: true,
      //   backdrop: 'static',
      //   animation: true,
      //   keyboard: false,
      //   backdropClass: 'modal-backdrop',
      //   windowClass: 'custom-overlay-container',
      // });
      this.loading = false;
    } catch (error) {
      this.loading = false;
      console.log('openEventModal', error);
    }
  }

  async getUpcomingEvents(isLoading = false) {
    try {
      if (isLoading) {
        this.loading = true;
        this.spinner.show();
      }

      const selectedSchoolByAdmin = this.storageService.getItem('selectedSchoolByAdmin');

      this.calendarService.schoolEventDate = this.viewDate;
      const dates = this.getStartAndEndOfCurrentMonth();
      const body = {
        start: dates.start,
        end: dates.end,
        school_id: selectedSchoolByAdmin ? selectedSchoolByAdmin.id : this.user.user.school_id,
      };

      /* If mockData is enabled we only want to include the static UPCOMING_EVENTS
       when the calendar is showing the ongoing/current month. Otherwise only
      use real events returned from the service.*/
      const fetchedEvents = await this.calendarService.getUpcomingUserEvents(body);
      this.upcomingEvents = this.processMockEvents(fetchedEvents);
      this.upcomingEvents = await this.showTakeHomeProjectEventsByClassroom(this.upcomingEvents, this.classroom);

      this.updateSchoolEvents();
      if (isLoading) {
        this.loading = false;
        this.spinner.hide();
      }
      this.setCurrentCalendarValues();
    } catch (error) {
      this.loading = false;
      this.spinner.hide();
      // console.log(error);
    }
  }

  setCurrentCalendarValues(): void {
    this.isCalendarStart =
      this.startDate &&
      this.startDate.getMonth() + ',' + this.startDate.getFullYear() ===
        this.viewDate.getMonth() + ',' + this.viewDate.getFullYear();
    this.isCalendarEnd =
      this.endDate &&
      this.endDate.getMonth() + ',' + this.endDate.getFullYear() ===
        this.viewDate.getMonth() + ',' + this.viewDate.getFullYear();
    if (!this.isCalendarStart && !this.isCalendarEnd) {
      this.isCalendarStart = false;
      this.isCalendarEnd = false;
    }
  }

  getStartAndEndOfCurrentMonth() {
    try {
      const start = dayjs(this.viewDate).startOf('month').valueOf();
      const end = dayjs(this.viewDate).endOf('month').valueOf();

      return {
        start,
        end,
      };
    } catch (error) {
      // console.log(error)
    }
  }

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  expandUpcomingEventsView(isClose = false) {
    try {
      const isExpand = isClose ? false : true;

      this.user && !this.storageService.checkUserRole(this.user.user.role_id, 'student')
        ? this.authService.closeDialog.emit(!isExpand)
        : null;
      this.studentService.isExpand.emit({
        compontentLabel: 'Admin School Events',
        isExpand,
      });
    } catch (error) {
      console.log(error);
    }
  }

  beforeMonthViewRender({ body }: { body: CalendarMonthViewDay[] }): void {
    try {
      body.forEach(async (day) => {
        day.events.map(
          (x: any) =>
            (x.color =
              x?.cssClass === 'green-event-content'
                ? {
                    primary: '#49debb',
                    secondary: '#49debb',
                  }
                : x?.cssClass === 'blue-event-content'
                  ? {
                      primary: '#63baf8',
                      secondary: '#63baf8',
                    }
                  : x?.cssClass === 'yellow-event-content'
                    ? {
                        primary: '#ffbc6e',
                        secondary: '#ffbc6e',
                      }
                    : x?.cssClass === 'purple-event-content'
                      ? {
                          primary: '#ad6ff3',
                          secondary: '#ad6ff3',
                        }
                      : ''),
        );
      });
    } catch (error) {
      // console.log(error);
    }
  }

  // public async submitFormValues(event): Promise<void> {
  //   try {
  //     // this.modalService.dismissAll(this.myModal);
  //     this.closeDialog();
  //     if (event) {
  //       if (this.isExpand) {
  //         this.closeDialog();
  //         await this.getUpcomingEvents();

  //       } else {
  //         this.closeDialog();
  //         await this.getUpcomingEvents();
  //       }
  //     }
  //   } catch (error) {
  //     // console.log(error);
  //   }
  // }

  public async submitFormValues(event: any): Promise<void> {
    try {
      if (event) {
        this.closeDialog();
        await this.getUpcomingEvents();

        if (this.upcomingEvents?.length > 0) {
          this.upcomingEvents = this.upcomingEvents.filter((event) => {
            if (this.tabTitle === 'All Events') {
              return event.user_id !== this.user?.user.id;
            } else {
              return event.user_id === this.user?.user.id;
            }
          });
          // Process the upcoming events for all types of users
          this.upcomingEvents = this.processMockEvents(
            this.upcomingEvents,
            this.mockData && this.user.user.role_id === ROLES.EXECUTIVE_DIRECTOR ? false : true,
          );
        }
      }
      this.closeDialog();
    } catch (error) {
      console.log('submitFormValues', error);
    }
  }

  async userTabChange(tabEvent?) {
    try {
      this.spinner.show();
      this.tabTitle = tabEvent.tabTitle;
      await this.getUpcomingEvents();
      if (this.upcomingEvents?.length > 0) {
        this.upcomingEvents = this.upcomingEvents.filter((event) => {
          if (tabEvent.tabTitle === 'All Events') {
            return event.user_id !== this.user?.user.id;
          } else {
            return event.user_id === this.user?.user.id;
          }
        });
      }
      this.spinner.hide();
    } catch (error) {
      console.error('Error occurred userTabChange:', error);
    }
  }

  closeDialog(): void {
    try {
      if (this.dialogRef) {
        this.dialogRef.close();
      }
    } catch (error) {
      console.log('closeDialog', error);
    }
  }

  /**
   * @description
   * Filters take-home project events based on the provided classroom.
   * If no classroom is provided, all events are returned.
   * Additionally, events with a classroom_id of 0 are always included as they are considered global events.
   *
   * @param events - Array of event objects to filter.
   * @param classroom - The classroom object used to filter events by its ID.
   * @returns Filtered array of events matching the classroom ID or having a classroom_id of 0.
   */
  showTakeHomeProjectEventsByClassroom(events: any[], classroom: any): any[] {
    if (!classroom || !classroom.id) {
      return events;
    }
    const classroomId = classroom.id;
    return events.filter((event) => event.classroom_id === classroomId || event.classroom_id === 0);
  }

  /**
   * @description Processes the events to include mock data if applicable.
   * @param events - Array of events to process.
   * @params actionPerformed - flag to check if user has perform any action or not
   * @returns AdminSchoolEvent[] - list of sorted school events
   */
  private processMockEvents(events: AdminSchoolEvent[], actionPerformed: boolean = false): AdminSchoolEvent[] {
    try {
      const isOngoingMonth = dayjs(this.viewDate).isSame(dayjs(), 'month');

      let updatedEvents = [...events];

      if (this.mockData && isOngoingMonth && !actionPerformed) {
        updatedEvents = [...UPCOMING_EVENTS, ...events];
      }

      // If the user is a teacher and mockData is enabled, filter the events to show only those relevant to the teacher's view.
      if (this.user.user.role_id === ROLES.TEACHER && this.mockData) {
        updatedEvents = updatedEvents.filter((event) => {
          if (this.tabTitle === 'All Events') {
            return event.user_id !== this.user?.user.id;
          } else {
            return event.user_id === this.user?.user.id;
          }
        });
      }

      return updatedEvents.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    } catch (error) {
      console.log('AdminSchoolEventsComponent ~ processMockEvents ~ error:', error);
    }
  }

  /**
   * @description Resets the calendar's current date to today's date when the component is destroyed. This ensures that when the user navigates away from the calendar and returns later, it will display the current month instead of the last viewed month.
   */
  ngOnDestroy(): void {
    this.calendarService.schoolEventDate = new Date();
  }
}
